import { Runtime, flow, pipe } from "fp";

import React from "react";
import styled, { css, keyframes } from "styled-components";

import * as Color from "~/Color";

import {
  ToggleOn,
  ToggleOff,
  Truck,
  TruckRamp,
  SteeringWheel,
  Bed,
} from "~/Icon";

import * as Layout from "~/Layout";
import { Order } from "~/Order";
import { Search } from "~/Search";
import * as Tooltip from "~/Tooltip";
import * as Typography from "~/Typography";

/**
 * `Status` as presented to the user does not map one-to-one with the FMCSA
 * notion of status. Real FMCSA statuses are defined in the `Actual` namespace,
 * whereas "derived status" types are defined by the `Derived` namespace.
 */
export type Status = Actual.Actual | Derived.Derived;

type Props = {
  readonly className?: string;
  readonly icon?: boolean;
  readonly text?: "full" | string | boolean;
  readonly strong?: boolean;
  readonly status: Status;
};

export const on = () => "ON" as const;
export const off = () => "OFF" as const;
export const drive = () => "D" as const;
export const yardMove = () => "YM" as const;
export const personalUse = () => "PU" as const;
export const sleeperBerth = () => "SB" as const;

export const toList = () =>
  [on(), off(), drive(), yardMove(), personalUse(), sleeperBerth()] as const;

export const when = function <A>(when: {
  readonly on: () => A;
  readonly off: () => A;
  readonly drive: () => A;
  readonly yardMove: () => A;
  readonly personalUse: () => A;
  readonly sleeperBerth: () => A;
}) {
  return (status: Status): A => {
    switch (status) {
      case on():
        return when.on();
      case off():
        return when.off();
      case drive():
        return when.drive();
      case yardMove():
        return when.yardMove();
      case personalUse():
        return when.personalUse();
      case sleeperBerth():
        return when.sleeperBerth();
    }
  };
};

/**
 * Could a duty period with this status have produced work?
 *
 * Work order, work codes and job are attributed to a duty row by finding the
 * time entry that overlaps it — but overlap alone does not mean the period
 * *earned* the work. A time entry can span a duty change, so an entry carrying
 * a work order set on going ON duty also overlaps the OFF period before it, and
 * both rows then show it. Off duty means not working, so the off-duty row must
 * stay blank however the intervals line up.
 *
 * Written with `when` rather than a list of the false cases so a status added
 * later has to make this decision explicitly instead of defaulting to "shows
 * work" — see the state-union note in CLAUDE.md.
 */
export const isOnDuty: (status: Status) => boolean = when({
  on: () => true,
  drive: () => true,
  // Derived from `on()` — a yard move is on-duty not driving.
  yardMove: () => true,
  off: () => false,
  sleeperBerth: () => false,
  // Derived from `off()` — personal conveyance is off-duty driving.
  personalUse: () => false,
});

export const { equals, compare } = Order.fromCompare<Status>((a, b) =>
  Order.number().compare(toNumber(a), toNumber(b))
);

const toNumber = when({
  on: () => 1,
  off: () => 0,
  drive: () => 5,
  yardMove: () => 4,
  personalUse: () => 3,
  sleeperBerth: () => 2,
});

export const toString = when({
  on: () => "On",
  off: () => "Off",
  drive: () => "Drive",
  yardMove: () => "Yard Move",
  personalUse: () => "Personal Use",
  sleeperBerth: () => "Sleeper Berth",
});

export const toSearch: Search.From<Status> = (status) =>
  Search.join(
    Search.string(status),
    status ? Search.string(toString(status)) : Search.empty()
  );

export const toColor = when({
  on: () => Color.green(),
  off: () => Color.steel(),
  drive: () => Color.azure(),
  yardMove: () => Color.azure(),
  personalUse: () => Color.azure(),
  sleeperBerth: () => Color.steel(),
});

export const Component: Component<Props> = ({
  className,
  icon = true,
  text = true,
  strong = true,
  status,
}) => (
  <Tooltip.Component Content={() => <>{toString(status)}</>}>
    <Container.Component className={className} status={status}>
      {icon && <Icon.Component status={status} />}
      {text && (
        <Layout.Inline.Component left>
          <Typography.Component strong={strong}>
            {text === "full" ? toString(status) : text === true ? status : text}
          </Typography.Component>
        </Layout.Inline.Component>
      )}
    </Container.Component>
  </Tooltip.Component>
);

namespace Container {
  export const Component = styled.span<Props>`
    & > svg {
      color: ${({ status }) => toColor(status)};
      ${flow(
        ({ status }) => status,
        when({
          on: () => [""],
          off: () => [""],
          drive: () => BouncingTruck.animation,
          yardMove: () => Dumping.animation,
          personalUse: () => SpinningSteeringWheel.animation,
          sleeperBerth: () => [""],
        })
      )}
    }
  `;
}

export namespace Icon {
  export const Component: Component<{ readonly status: Status }> = ({
    status,
  }) =>
    pipe(
      status,
      when({
        on: () => <ToggleOn />,
        off: () => <ToggleOff />,
        drive: () => <Truck />,
        yardMove: () => <TruckRamp />,
        personalUse: () => <SteeringWheel />,
        sleeperBerth: () => <Bed />,
      })
    );
}

namespace BouncingTruck {
  const translate = "0.02em";
  const frames = keyframes`
    0% { transform: translateY(${translate}); }
    100% { transform: translateY(-${translate}); }
  `;

  export const animation = css`
    animation-name: ${frames};
    animation-duration: 0.8s;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
    animation-direction: alternate;
  `;
}

namespace SpinningSteeringWheel {
  const rotate = "35deg";
  const frames = keyframes`
    0% { transform: rotate(${rotate}); }
    100% { transform: rotate(-${rotate}); }
  `;

  export const animation = css`
    animation-name: ${frames};
    animation-duration: 10s;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
    animation-direction: alternate;
  `;
}

namespace Dumping {
  const frames = keyframes`
    0% { transform: rotate(0); }
    100% { transform: rotate(-10deg); }
  `;

  export const animation = css`
    transform-origin: 100% 100%;
    animation-name: ${frames};
    animation-duration: 6s;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
    animation-direction: alternate;
  `;
}

export namespace Response {
  export const runtime = () =>
    Runtime.intersection([
      Actual.Response.runtime(),
      ExceptionType.Response.runtime(),
    ]);

  export const decode = (response: Runtime.ToType<typeof runtime>): Status =>
    ExceptionType.toStatus(
      response.status,
      ExceptionType.Response.toExceptionType(response)
    );
}

export namespace Actual {
  export type Actual = ReturnType<
    typeof on | typeof off | typeof drive | typeof sleeperBerth
  >;

  export const fromStatus = (status: Status): Actual => {
    switch (status) {
      case yardMove():
        return on();
      case personalUse():
        return off();
      default:
        return status;
    }
  };

  export namespace Response {
    export const runtime = () =>
      Runtime.strict({
        status: Runtime.union([
          Runtime.literal(on()),
          Runtime.literal(off()),
          Runtime.literal(drive()),
          Runtime.literal(sleeperBerth()),
        ]),
      });
  }
}

export namespace Derived {
  /**
   * - `YARD_MOVE` is derived from an `Event` with an `ExceptionType` of `YM`
   *   and an `Actual` status of `on()`.
   * - `PERSONAL_USE` is derived from an `Event` with an `ExceptionType` of `PU`
   *   and an `Actual` status of `OFF`.
   */
  export type Derived = ReturnType<typeof yardMove | typeof personalUse>;
}

export namespace ExceptionType {
  export type ExceptionType =
    | ReturnType<typeof yardMove | typeof personalUse>
    | undefined;

  export const toStatus = (
    actual: Actual.Actual,
    exceptionType: ExceptionType
  ): Status => {
    switch (actual) {
      case on():
        return exceptionType === yardMove() ? exceptionType : on();
      case off():
        return exceptionType === personalUse() ? exceptionType : off();
      default:
        return actual;
    }
  };

  export const fromStatus = (status: Status): ExceptionType | undefined => {
    if (status === yardMove() || status === personalUse()) return status;
  };

  export namespace Response {
    export const runtime = () =>
      Runtime.strict({
        exception_type: Runtime.nullable(
          Runtime.union([
            Runtime.literal(yardMove()),
            Runtime.literal(personalUse()),
            Runtime.literal(""),
          ])
        ),
      });

    export const toExceptionType = (
      response: Runtime.ToType<typeof runtime>
    ): ExceptionType =>
      response.exception_type ? response.exception_type : undefined;
  }
}
